/**
 * Reward Center — Campaign Manager V2 contract.
 *
 * Two layers live here:
 *   • `*Dto` types mirror the wire format exactly (snake_case, decimals as
 *     strings, `int64` as strings). They are what the back end documents, so
 *     they can be diffed against the guide without guessing.
 *   • The camelCase types below them are what the UI consumes, produced by
 *     `normalize.ts`. Renaming is all that happens there — the V2 DTOs are
 *     already presentation-ready and the guide is explicit that the front end
 *     must not re-derive status, progress or claimability itself.
 *
 * Money, progress, ticket counts and weights stay `DecimalString` end to end.
 * Never put them through `Number` for anything but display width decisions.
 */

/** An exact decimal carried as a string, e.g. `"500000000"`. */
export type DecimalString = string;

/** An `int64` carried as a string, e.g. `remaining_seconds: "172800"`. */
export type Int64String = string;

/** RFC 3339 UTC timestamp, or null. */
export type Timestamp = string | null;

/* -------------------------------------------------------------------------- */
/* Envelopes                                                                  */
/* -------------------------------------------------------------------------- */

/** Every successful business response carries exactly one `data` key. */
export interface EnvelopeDto<T> {
  data: T;
}

export interface ListBodyDto<T> {
  items: T[];
  page: number;
  page_limit: number;
  total: number;
}

export type ListEnvelopeDto<T> = EnvelopeDto<ListBodyDto<T>>;

/** Normalized page. `totalPages` is derived — V2 never sends it. */
export interface Paged<T> {
  items: T[];
  page: number;
  pageLimit: number;
  total: number;
  totalPages: number;
}

export interface PageParams {
  /** 1-based. Defaults to 1 server-side. */
  page?: number;
  /** 1..100, defaults to 20 server-side. */
  pageLimit?: number;
}

/* -------------------------------------------------------------------------- */
/* Enums                                                                      */
/* -------------------------------------------------------------------------- */

/** Time-window state. This is what active/inactive badges and filters use. */
export const ActivityStatusValues = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
} as const;
export type ActivityStatus =
  typeof ActivityStatusValues[keyof typeof ActivityStatusValues];

/** `activity_status` query filters also accept ALL. */
export type ActivityStatusFilter = ActivityStatus | 'ALL';

/** Definition lifecycle — not the same thing as `activity_status`. */
export const CampaignStatusValues = {
  SCHEDULED: 'SCHEDULED',
  ACTIVE: 'ACTIVE',
  FINISHED: 'FINISHED',
  CANCELLED: 'CANCELLED'
} as const;
export type CampaignStatus =
  typeof CampaignStatusValues[keyof typeof CampaignStatusValues];

export const UserTaskStatusValues = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  CLAIMABLE: 'CLAIMABLE',
  CLAIMED: 'CLAIMED',
  EXPIRED: 'EXPIRED'
} as const;
export type UserTaskStatus =
  typeof UserTaskStatusValues[keyof typeof UserTaskStatusValues];

export type UserTaskStatusFilter = UserTaskStatus | 'ALL';

export const ClaimStatusValues = {
  /** Rewards land automatically — render no claim button at all. */
  NOT_REQUIRED: 'NOT_REQUIRED',
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  LOCKED_BY_CAMPAIGN: 'LOCKED_BY_CAMPAIGN',
  CLAIMABLE: 'CLAIMABLE',
  CLAIMED: 'CLAIMED',
  EXPIRED: 'EXPIRED'
} as const;
export type ClaimStatus =
  typeof ClaimStatusValues[keyof typeof ClaimStatusValues];

export const ClaimModeValues = {
  AUTO: 'AUTO',
  CLAIM_REQUIRED: 'CLAIM_REQUIRED'
} as const;
export type ClaimMode = typeof ClaimModeValues[keyof typeof ClaimModeValues];

export const RecurrenceValues = {
  ONCE: 'ONCE',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY'
} as const;
export type Recurrence = typeof RecurrenceValues[keyof typeof RecurrenceValues];

/**
 * CTA transport.
 *
 * `ROUTE` carries a web path and `URL` an absolute link — neither is a stable
 * semantic destination id, so a native client cannot map them to a screen
 * without parsing web paths. See `cta.ts` for how far we can go on web and the
 * hand-off note for Android.
 */
export const ActionTypeValues = {
  ROUTE: 'ROUTE',
  URL: 'URL',
  NONE: 'NONE'
} as const;
export type ActionType = typeof ActionTypeValues[keyof typeof ActionTypeValues];

/**
 * Stable semantic destination for a ROUTE action.
 *
 * The back end sends this as `android_route`. Despite the name it is not
 * Android-specific — it is the only route-change-proof identifier in the
 * contract, so web resolves it to its own path too and falls back to
 * `action.value` for values it does not recognize.
 *
 * Add a member here (and its route in `TaskList`) as the back end confirms
 * each value; unknown strings resolve to UNSPECIFIED and use `value`.
 */
export const TaskDestinationValues = {
  UNSPECIFIED: 'UNSPECIFIED',
  KYC: 'ANDROID_ROUTE_KYC',
  DEPOSIT_FIAT: 'ANDROID_ROUTE_DEPOSIT_FIAT',
  DEPOSIT_CRYPTO: 'ANDROID_ROUTE_DEPOSIT_CRYPTO',
  TRADE: 'ANDROID_ROUTE_TRADE'
} as const;
export type TaskDestination =
  typeof TaskDestinationValues[keyof typeof TaskDestinationValues];

const KNOWN_DESTINATIONS: string[] = Object.values(TaskDestinationValues);

export const toTaskDestination = (value?: string | null): TaskDestination =>
  value && KNOWN_DESTINATIONS.includes(value)
    ? (value as TaskDestination)
    : TaskDestinationValues.UNSPECIFIED;

export const HistoryTypeValues = {
  TASK: 'TASK',
  REWARD: 'REWARD',
  CAMPAIGN: 'CAMPAIGN'
} as const;
export type HistoryType =
  typeof HistoryTypeValues[keyof typeof HistoryTypeValues];

/**
 * Reward assets are free-form strings on the wire. `LOTTERY_TICKET` is the only
 * one with special behaviour (it carries `unit_weight`); the rest are wallet
 * assets such as `USDT` / `TMN`.
 */
export const LOTTERY_TICKET_ASSET = 'LOTTERY_TICKET';

/* -------------------------------------------------------------------------- */
/* Wire DTOs                                                                  */
/* -------------------------------------------------------------------------- */

export interface RewardDto {
  key: string;
  asset: string;
  amount: DecimalString;
  /** Only meaningful for LOTTERY_TICKET; defaults to "1" server-side. */
  unit_weight?: DecimalString;
  /** Comes from `reward.metadata.display_name`. */
  display_name?: string;
}

export interface CampaignProgressDto {
  required_completed: number;
  required_total: number;
  optional_completed: number;
  optional_required: number;
  is_completed: boolean;
  completed_at: Timestamp;
}

export interface TaskCountsDto {
  total: number;
  active: number;
  completed: number;
  claimable: number;
}

export interface CampaignViewDto {
  id: string;
  slug: string;
  title: string;
  is_special: boolean;
  tags: string[];
  activity_status: ActivityStatus;
  status: CampaignStatus;
  starts_at: Timestamp;
  ends_at: Timestamp;
  remaining_seconds: Int64String;
  progress: CampaignProgressDto;
  rewards: RewardDto[];
  task_counts: TaskCountsDto;
  /** Untyped by protobuf — every field needs a fallback. See `CampaignMeta`. */
  metadata: Record<string, unknown>;
  recurrence: Recurrence;
  cycle_key: string;
  cycle_starts_at: Timestamp;
  cycle_ends_at: Timestamp;
}

export interface TaskProgressDto {
  current: DecimalString;
  target: DecimalString;
  remaining: DecimalString;
  /** A plain number, unlike the decimal fields around it. */
  percent: number;
  unit: string;
  status?: string;
}

export interface TaskClaimDto {
  /** Present per the guide's field table; treat as optional defensively. */
  required?: boolean;
  status: ClaimStatus;
}

export interface TaskActionDto {
  type: ActionType;
  value: string;
  label: string;
  /** Semantic destination; see TaskDestinationValues. */
  android_route?: string;
}

/**
 * What a public task asks for (`PublicTaskView.goal`); the same values as
 * `progress.target` / `progress.unit` in a personal TaskView.
 */
export interface TaskGoalDto {
  target: DecimalString;
  unit: string;
}

/**
 * Both `TaskView` (personal) and `PublicTaskView` (guest). A public task has no
 * `user_status`, `progress`, `claim` or `reward_locked_by_campaign`, and adds
 * `goal` and `claim_required` in their place.
 */
export interface TaskViewDto {
  id: string;
  campaign_id?: string;
  title: string;
  subtitle?: string;
  category?: string;
  activity_status: ActivityStatus;
  /** Absent for a guest. */
  user_status?: UserTaskStatus;
  starts_at: Timestamp;
  ends_at: Timestamp;
  remaining_seconds: Int64String;
  progress?: TaskProgressDto;
  claim?: TaskClaimDto;
  rewards?: RewardDto[];
  action?: TaskActionDto;
  instructions?: string[];
  image_url?: string;
  self_completable: boolean;
  /** Absent for a guest. */
  reward_locked_by_campaign?: boolean;
  /**
   * The task's reward is released only once the whole campaign is completed.
   * A property of the task; `reward_locked_by_campaign` says whether it is
   * holding back this user's reward right now.
   */
  reward_requires_campaign_completion?: boolean;
  /** Public tasks only: what the task asks for. */
  goal?: TaskGoalDto;
  /** Public tasks only: the same as `claim.required`. */
  claim_required?: boolean;
  claim_mode: ClaimMode;
  recurrence?: Recurrence;
  cycle_key?: string;
  /** Presentation keys are lifted out of metadata server-side. */
  metadata?: Record<string, unknown>;
}

/** Response of both `/complete` and `/claim`. Authoritative post-mutation. */
export interface TaskMutationResultDto {
  already_applied: boolean;
  task: TaskViewDto;
  rewards: RewardDto[];
}

export interface LotteryPrizeDto {
  key: string;
  winner_count: number;
  asset: string;
  amount: DecimalString;
}

export interface LotterySummaryDto {
  cycle_key: string;
  cycle_starts_at: Timestamp;
  cycle_ends_at: Timestamp;
  user_ticket_count: DecimalString;
  user_effective_weight: DecimalString;
  participant_count: number;
  total_ticket_count: DecimalString;
  total_effective_weight: DecimalString;
  drawn: boolean;
  /** Empty string until the cycle is drawn. */
  draw_id: string;
  drawn_at: Timestamp;
  /** Always `[]` before the draw — planned prizes have no source in V2. */
  prizes: LotteryPrizeDto[];
  remaining_seconds: Int64String;
}

export interface LotteryListItemDto {
  campaign_id: string;
  campaign_title: string;
  cycle_key: string;
  draw_id: string;
  drawn_at: Timestamp;
  participant_count: number;
  prizes: LotteryPrizeDto[];
}

/**
 * Winners are documented only as "masked identities and prize data", with
 * `masked_identity` the one named field and `user_id` never exposed. The
 * endpoint is also known-broken pending a back-end fix, so everything except
 * `masked_identity` is optional and unverified.
 */
export interface LotteryWinnerDto {
  masked_identity?: string;
  prize_key?: string;
  asset?: string;
  amount?: DecimalString;
  ticket_count?: DecimalString;
  [key: string]: unknown;
}

/**
 * History items are a union tagged by `type`; only the matching field is set.
 * The guide shows the envelope but never the inner object shapes, so TASK is
 * read as a TaskView-shaped object and the other two are kept loose.
 */
export interface HistoryItemDto {
  type: HistoryType;
  task: TaskHistoryEntryDto | null;
  reward: RewardHistoryEntryDto | null;
  campaign: CampaignHistoryEntryDto | null;
}

/**
 * TASK history item as the back end sends it (`TaskHistoryItem` proto). It is
 * NOT a TaskView: titles are `task_title` / `campaign_title`, dates are
 * `completed_at` / `updated_at` / `window_ends_at`, and there is no subtitle
 * or reward list — those are joined in on the client.
 */
export interface TaskHistoryEntryDto {
  campaign_id?: string;
  campaign_title?: string;
  task_id?: string;
  task_title?: string;
  cycle_key?: string;
  activity_status?: ActivityStatus;
  user_status?: UserTaskStatus;
  progress?: TaskProgressDto;
  claim?: TaskClaimDto;
  completed_at?: Timestamp;
  updated_at?: Timestamp;
  window_ends_at?: Timestamp;
}

/** Shape unconfirmed — see the back-end follow-up list. */
export interface RewardHistoryEntryDto {
  key?: string;
  asset?: string;
  amount?: DecimalString;
  display_name?: string;
  status?: string;
  /** TASK / CAMPAIGN / LOTTERY — LOTTERY marks a draw prize. */
  source_type?: string;
  task_id?: string;
  campaign_id?: string;
  campaign_title?: string;
  cycle_key?: string;
  /** Always the campaign cycle, even for a task reward (see `cycle_key`). */
  campaign_cycle_key?: string;
  granted_at?: Timestamp;
  issued_at?: Timestamp;
  created_at?: Timestamp;
  [key: string]: unknown;
}

/** Shape unconfirmed — see the back-end follow-up list. */
export interface CampaignHistoryEntryDto {
  campaign_id?: string;
  campaign_title?: string;
  cycle_key?: string;
  completed_at?: Timestamp;
  rewards?: RewardDto[];
  [key: string]: unknown;
}

export interface SpecialCampaignEnvelopeDto {
  /** Null is the normal "no active special campaign" answer, not an error. */
  campaign: CampaignViewDto | null;
}

/* -------------------------------------------------------------------------- */
/* Domain types (what the UI renders)                                         */
/* -------------------------------------------------------------------------- */

export interface Reward {
  key: string;
  asset: string;
  amount: DecimalString;
  unitWeight: DecimalString;
  /** Falls back to a locally composed label when metadata has none. */
  displayName: string;
  isLotteryTicket: boolean;
}

export interface CampaignProgress {
  requiredCompleted: number;
  requiredTotal: number;
  optionalCompleted: number;
  optionalRequired: number;
  isCompleted: boolean;
  completedAt: Timestamp;
}

export interface TaskCounts {
  total: number;
  active: number;
  completed: number;
  claimable: number;
}

/**
 * Conventional campaign metadata. Nothing here is validated by protobuf, so
 * every field is optional and the UI must have a fallback for each.
 */
export interface CampaignMeta {
  imageUrl?: string;
  description?: string;
  rulesTitle?: string;
  rules: string[];
  rulesUrl?: string;
  heroTitle?: string;
  heroSubtitle?: string;
  /**
   * DISPLAY ONLY — `presentation.prize_preview` in campaign metadata.
   *
   * The API carries draw prizes only AFTER a cycle is drawn, so there is no
   * authoritative "this week's prize" before the draw. This string lets the
   * hero card announce the intended prize in the meantime. It is free text
   * the backend never validates, and it never influences the draw or any
   * payout — real prizes always come from `LotterySummary.prizes` once
   * `drawn` is true. Drop this once the backend grows a typed planned-prize
   * contract (integration guide §15, row 1).
   */
  prizePreview?: string;
  /**
   * Icon shown beside `prizePreview`, from `presentation.prize_icon` — any
   * image URL. Purely decorative; it never implies which asset the draw pays.
   */
  prizeIcon?: string;
  /**
   * Asset symbol for the announced prize (`presentation.prize_asset`, e.g.
   * "USDT"). Used only to pick a bundled coin icon when `prizeIcon` is not
   * set, so a campaign needs no external image to match the design.
   */
  prizeAsset?: string;
}

export interface Campaign {
  id: string;
  slug: string;
  title: string;
  isSpecial: boolean;
  tags: string[];
  activityStatus: ActivityStatus;
  status: CampaignStatus;
  startsAt: Timestamp;
  endsAt: Timestamp;
  remainingSeconds: number;
  progress: CampaignProgress;
  rewards: Reward[];
  taskCounts: TaskCounts;
  recurrence: Recurrence;
  cycleKey: string;
  cycleStartsAt: Timestamp;
  cycleEndsAt: Timestamp;
  meta: CampaignMeta;
}

export interface TaskProgress {
  current: DecimalString;
  target: DecimalString;
  remaining: DecimalString;
  percent: number;
  unit: string;
}

/** What a task asks for: the target and its unit, without the user's progress. */
export interface TaskGoal {
  target: DecimalString;
  unit: string;
}

export interface TaskClaim {
  required: boolean;
  status: ClaimStatus;
}

export interface TaskAction {
  type: ActionType;
  /** The web path as sent. Only used when `destination` is UNSPECIFIED. */
  value: string;
  label: string;
  destination: TaskDestination;
}

export interface Task {
  id: string;
  campaignId: string;
  title: string;
  subtitle: string;
  category: string;
  activityStatus: ActivityStatus;
  userStatus: UserTaskStatus;
  startsAt: Timestamp;
  endsAt: Timestamp;
  remainingSeconds: number;
  /** The user's progress; always null for a guest. */
  progress: TaskProgress | null;
  /** What the task asks for; known for guests and users alike. */
  goal: TaskGoal | null;
  claim: TaskClaim;
  rewards: Reward[];
  action: TaskAction | null;
  instructions: string[];
  imageUrl: string;
  /** The only client-visible permission to call `/complete`. */
  selfCompletable: boolean;
  rewardLockedByCampaign: boolean;
  /** The reward is released only once the whole campaign is completed. */
  rewardRequiresCampaignCompletion: boolean;
  claimMode: ClaimMode;
  recurrence: Recurrence;
  cycleKey: string;
  /**
   * Non-presentation metadata keys the server passes through as-is. Only read
   * defensively (see `eligibility.ts`); optional so hand-built tasks and
   * fixtures stay valid.
   */
  metadata?: Record<string, unknown>;
}

export interface TaskMutationResult {
  alreadyApplied: boolean;
  task: Task;
  rewards: Reward[];
}

export interface LotteryPrize {
  key: string;
  winnerCount: number;
  asset: string;
  amount: DecimalString;
}

export interface LotterySummary {
  cycleKey: string;
  cycleStartsAt: Timestamp;
  cycleEndsAt: Timestamp;
  userTicketCount: DecimalString;
  userEffectiveWeight: DecimalString;
  participantCount: number;
  totalTicketCount: DecimalString;
  totalEffectiveWeight: DecimalString;
  drawn: boolean;
  drawId: string;
  drawnAt: Timestamp;
  prizes: LotteryPrize[];
  remainingSeconds: number;
}

export interface Lottery {
  campaignId: string;
  campaignTitle: string;
  cycleKey: string;
  drawId: string;
  drawnAt: Timestamp;
  participantCount: number;
  prizes: LotteryPrize[];
}

export interface LotteryWinner {
  /** `****` until identity resolution completes server-side. */
  maskedIdentity: string;
  prizeKey: string;
  asset: string;
  amount: DecimalString;
}

export interface TaskHistoryEntry {
  campaignId: string;
  campaignTitle: string;
  taskId: string;
  title: string;
  cycleKey: string;
  activityStatus: ActivityStatus;
  userStatus: UserTaskStatus;
  claimStatus: ClaimStatus;
  completedAt: Timestamp;
  updatedAt: Timestamp;
}

export interface RewardHistoryEntry {
  key: string;
  asset: string;
  amount: DecimalString;
  displayName: string;
  status: string;
  /** TASK / CAMPAIGN / LOTTERY; LOTTERY is a draw prize. */
  sourceType: string;
  taskId: string;
  campaignId: string;
  campaignTitle: string;
  /** Task cycle for a task reward, campaign cycle otherwise. */
  cycleKey: string;
  /** The campaign cycle the reward belongs to — what the lottery counts. */
  campaignCycleKey: string;
  grantedAt: Timestamp;
}

export interface CampaignHistoryEntry {
  campaignId: string;
  campaignTitle: string;
  cycleKey: string;
  completedAt: Timestamp;
  rewards: Reward[];
}

export type HistoryItem =
  | { type: 'TASK'; task: TaskHistoryEntry }
  | { type: 'REWARD'; reward: RewardHistoryEntry }
  | { type: 'CAMPAIGN'; campaign: CampaignHistoryEntry };

/* -------------------------------------------------------------------------- */
/* Query params                                                               */
/* -------------------------------------------------------------------------- */

/**
 * Reads that exist in both modes take `guest`: true calls the public route (no
 * personal fields), anything else the personal one (needs a login). The screen
 * picks it from its login state; RTK Query caches the two modes apart.
 */
export interface GuestParam {
  guest?: boolean;
}

export interface CampaignListParams extends PageParams, GuestParam {
  /** Server default is ACTIVE. */
  activityStatus?: ActivityStatusFilter;
  /** UPPER_SNAKE display tag, e.g. DAILY / WEEKLY / REFERRAL / SPECIAL. */
  tag?: string;
}

export interface TaskListParams extends PageParams, GuestParam {
  campaignId: string;
  activityStatus?: ActivityStatusFilter;
  /** Personal list only; the public one has no user status. */
  userStatus?: UserTaskStatusFilter;
  category?: string;
}

export interface TaskRefParams {
  campaignId: string;
  taskId: string;
}

export interface ClaimTaskParams extends TaskRefParams {
  /** Omit to let the server derive the current cycle. */
  cycleKey?: string;
}

export interface HistoryParams extends PageParams {
  /** Server default is TASK. */
  type?: HistoryType;
  activityStatus?: ActivityStatusFilter;
}

export interface LotterySummaryParams extends GuestParam {
  campaignId: string;
  /** Omit for the current campaign cycle. */
  cycleKey?: string;
}

export interface LotteryWinnersParams extends PageParams {
  campaignId: string;
  cycleKey: string;
}
